<template>
  <div class="creation-page">
    <header class="creation-head">
      <h1>{{ $t('creation.title') }}</h1>
      <p>{{ $t(`creation.intro.${active.id}`) }}</p>
    </header>

    <!-- 来源切换：顺序、名称跟侧边栏"创作工具"一致 -->
    <nav class="creation-tabs" :aria-label="$t('sidebar.create')">
      <router-link
        v-for="item in tools"
        :key="item.id"
        :to="`/create/${item.id}`"
        :class="{ 'is-active': tool === item.id }"
        :aria-current="tool === item.id ? 'page' : undefined"
      >
        {{ $t(`sidebar.${item.label}`) }}
      </router-link>
    </nav>

    <component
      :is="active.component"
      :key="active.id"
      :user-details="userDetails"
      @file-generated="finish"
      @record-success="finish"
    />

    <!-- 页脚一行小字：额度，以及当前套餐对这种来源的单次上限 -->
    <p v-if="user" class="creation-note">
      <span>{{ $t('creation.balance') }} <strong>{{ (userDetails.remainingUsage || 0).toLocaleString() }}</strong></span>
      <span v-if="active.limit">{{ $t(`premium.${planKey}.specs.${active.limit}`) }}</span>
      <button type="button" class="creation-link" @click="handleUpgrade('credits')">{{ $t('creation.buy') }}</button>
    </p>
    <p v-else class="creation-note">
      <span>{{ $t('creation.loginHint') }}</span>
      <button type="button" class="creation-link" @click="showLoginModal">{{ $t('navbar.login') }}</button>
    </p>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useAuth } from '@/composables/auth'
import { useViews } from '@/composables/views'
import { useModals } from '@/composables/modals'
import AudioUploader from './AudioUploader.vue'
import YouTubeImporter from './YouTubeImporter.vue'
import TextToSpeechUploader from './TextToSpeechUploader.vue'

// limit：购买页套餐说明里对应这种来源的那一项上限
const tools = [
  { id: 'youtube', label: 'YouTubeImport', component: YouTubeImporter },
  { id: 'transcribe', label: 'Transcribe', component: AudioUploader, limit: 'upload' },
  { id: 'speech', label: 'TextToSpeech', component: TextToSpeechUploader, limit: 'text' }
]

const props = defineProps({ tool: { type: String, required: true } })
const { user, userDetails } = useAuth()
const { handleViewChange } = useViews()
const { handleUpgrade, showLoginModal } = useModals()

const active = computed(() => tools.find(item => item.id === props.tool))
// 有永久额度的用户按 Pro 的上限算，和后端一致
const planKey = computed(() => (
  userDetails.value.isPremium ? 'premiumPlan' : userDetails.value.isPro ? 'proPlan' : 'freePlan'
))
const finish = () => handleViewChange('RecentFiles', { refresh: true })
</script>

<style scoped>
/* 一列居中、大标题、留白充足；页面上只有输入区和设置分组两个面板 */
.creation-page {
  width: min(680px, 100%);
  margin: 0 auto;
  padding: var(--space-8) 0 var(--space-12);
  color: var(--ink-primary);
}

.creation-head {
  text-align: center;
}

.creation-head h1 {
  margin: 0;
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.creation-head p {
  margin: var(--space-3) 0 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-md);
  line-height: 1.6;
}

.creation-tabs {
  display: flex;
  gap: 2px;
  width: fit-content;
  max-width: 100%;
  margin: var(--space-6) auto var(--space-8);
  padding: 2px;
  border-radius: var(--radius-md);
  background: rgba(16, 24, 32, 0.06);
}

.creation-tabs a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 112px;
  height: 34px;
  padding: 0 var(--space-4);
  border-radius: var(--radius-sm);
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
}

.creation-tabs a:hover {
  color: var(--ink-primary);
}

.creation-tabs a.is-active {
  background: var(--surface-panel);
  color: var(--ink-primary);
  font-weight: 600;
  box-shadow: var(--shadow-sm), 0 0 0 0.5px rgba(16, 24, 32, 0.08);
}

.creation-note {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-1) var(--space-4);
  margin: var(--space-8) 0 0;
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  line-height: 1.6;
}

.creation-note strong {
  color: var(--ink-secondary);
  font-family: var(--font-family-data);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}

.creation-link {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--signal-active-strong);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.creation-link:hover {
  text-decoration: underline;
}

.creation-tabs a:focus-visible,
.creation-link:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

@media (max-width: 640px) {
  .creation-page {
    padding-top: var(--space-4);
  }

  .creation-tabs {
    width: 100%;
  }

  .creation-tabs a {
    flex: 1;
    min-width: 0;
    padding: 0 var(--space-2);
  }
}
</style>
